import React, { useCallback, useContext } from 'react';
import { UiContext } from 'shared/lib/contexts/uiContext';
import { Badge, Box, Typography, useTheme } from '@mui/material';
import IconExit from 'shared/icons/exit';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import IconTrash from 'shared/icons/trash';
import MainMenuControl from './mainMenuControl';
import MainMenuSection from './mainMenuSection';
import { MobxContext } from 'shared/lib/contexts/mobxContext';
import { useSearchParamsUtils } from 'app/hooks/useSearchParamsUtils';
import { useNavigate } from 'react-router-dom';
import IconAdmin from 'shared/icons/iconAdmin';
import { defaultSpacingPresets } from 'shared/hooks/useGetSpacing';
import { useBooleanScreens } from 'shared/hooks/useBooleanScreens';

export default observer(function MainMenuModal({
  handleLogout,
  handleCloseMainMenu,
}: {
  handleLogout: () => void;
  handleCloseMainMenu: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const {
    spacings: { sp },
  } = useContext(UiContext);
  const {
    mobxStore: { sectionStore, authStore, adminRequestsStore },
  } = useContext(MobxContext);

  const { getSectionSearchParams } = useSearchParamsUtils();

  const { isLessLaptop } = useBooleanScreens();

  const handleClickSection = useCallback(
    (id: string) => {
      navigate({
        pathname: '/explorer',
        search: getSectionSearchParams({
          sectionId: id,
        }),
      });
      handleCloseMainMenu();
    },
    [getSectionSearchParams, handleCloseMainMenu, navigate]
  );

  const p = defaultSpacingPresets.sp.s70;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: sp.s80,
        py: p,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Typography
          sx={{ px: p, mb: sp.s20 }}
          variant="f12"
          color="custom.textInactive"
        >
          {t('section.sections')}
        </Typography>
        {sectionStore.sections?.map(
          ({ data }) =>
            data && (
              <MainMenuSection
                key={data.id}
                onClick={() => handleClickSection(data.id)}
              >
                <Typography variant="f18">{data.name}</Typography>
              </MainMenuSection>
            )
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          px: p,
          gap: sp.s30,
        }}
      >
        {authStore.user?.isAdmin ? (
          <>
            <MainMenuControl
              onClick={() => {
                navigate('/admin', { replace: false });
                handleCloseMainMenu();
              }}
            >
              <IconAdmin
                style={{ minWidth: 18, minHeight: 18 }}
                sizeKey="24x24"
                extColor={theme.palette.custom.textInactive}
              ></IconAdmin>
              <Typography variant="f16">
                {t('breadcrumbs.adminPanel')}
              </Typography>
              {adminRequestsStore.requests?.length ? (
                <Badge
                  sx={{
                    'ml': 'auto',

                    '& .MuiBadge-badge': {
                      position: 'static',
                      transform: 'none',
                    },
                  }}
                  badgeContent={adminRequestsStore.requests.length}
                  color="primary"
                ></Badge>
              ) : null}
            </MainMenuControl>
            {!isLessLaptop && (
              <MainMenuControl
                onClick={() => {
                  navigate('/trash', { replace: false });
                  handleCloseMainMenu();
                }}
              >
                <IconTrash
                  style={{ minWidth: 18, minHeight: 18 }}
                  sizeKey="24x24"
                  extColor={theme.palette.custom.textInactive}
                ></IconTrash>
                <Typography variant="f16">{t('common.trash')}</Typography>
              </MainMenuControl>
            )}
          </>
        ) : null}

        <MainMenuControl onClick={handleLogout}>
          <IconExit
            style={{ minWidth: 18, minHeight: 18 }}
            sizeKey="24x24"
            extColor={theme.palette.custom.textInactive}
          ></IconExit>
          <Typography variant="f16">{t('common.logout')}</Typography>
        </MainMenuControl>
      </Box>
    </Box>
  );
});
